import React from 'react';
import { motion } from 'framer-motion';
import { 
  SparklesIcon,
  RocketLaunchIcon,
  HeartIcon,
  LightBulbIcon,
  UserGroupIcon,
  BoltIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

const AboutUs: React.FC = () => {
  const values = [
    {
      icon: SparklesIcon,
      title: 'Free Forever',
      description: 'We believe PDF tools should be accessible to everyone. No paywalls, no subscriptions, just free tools for all.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Privacy First',
      description: 'Your files never leave your device. Everything processes in your browser, ensuring complete privacy and security.'
    },
    {
      icon: BoltIcon,
      title: 'Google Sign in',
      description: 'Simple authentication for web access. Your files stay private and secure - processing happens in your browser.'
    },
    {
      icon: HeartIcon,
      title: 'Built for Students',
      description: 'Created by students who understand the struggle of expensive PDF tools and the need for privacy.'
    }
  ];

  const features = [
    {
      icon: RocketLaunchIcon,
      title: 'Browser-Based Processing',
      description: 'All PDF processing happens locally in your browser using JavaScript. No servers, no uploads, complete control.'
    },
    {
      icon: LightBulbIcon,
      title: 'Open & Transparent',
      description: 'Our approach is open and verifiable. You can inspect the code to confirm we don\'t collect or store your data.'
    },
    {
      icon: GlobeAltIcon,
      title: 'Works Offline',
      description: 'Once loaded, NotesCrafter works even without internet. Process PDFs anywhere, anytime.'
    }
  ];

  const stats = [
    { value: '100%', label: 'Free' },
    { value: '0', label: 'Data Collected' },
    { value: 'Local', label: 'Processing' },
    { value: 'No', label: 'Login Required' }
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-4">
            About <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">NotesCrafter</span>
          </h1>
          <p className="text-xl text-text-muted max-w-3xl mx-auto">
            Free, privacy-first PDF tools that work entirely in your browser
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-2xl p-8 md:p-12 mb-12"
        >
          <div className="flex items-start space-x-4 mb-6">
            <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-text-main mb-4">Our Mission</h2>
              <p className="text-lg text-text-muted leading-relaxed mb-4">
                NotesCrafter was born from a simple frustration: why should processing PDFs require uploading files to strangers' servers, creating accounts, or paying monthly fees?
              </p>
              <p className="text-lg text-text-muted leading-relaxed mb-4">
                We built NotesCrafter to be different. Using modern browser technology, we process everything locally on your device. Your PDFs never leave your computer. No accounts, no tracking, no data collection.
              </p>
              <p className="text-lg text-text-muted leading-relaxed">
                <strong>100% free. 100% private. Forever.</strong> That's our commitment to students, professionals, and anyone who needs to work with PDFs.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <div key={index} className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-text-muted text-sm md:text-base">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Our Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-text-main mb-8 text-center">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-xl p-6 hover:border-primary/50 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-text-main mb-2">{value.title}</h3>
                      <p className="text-text-muted">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* What We Do */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-text-main mb-8 text-center">What We Do</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
                  <div className="bg-gradient-to-r from-primary to-secondary p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-text-main mb-3">{feature.title}</h3>
                  <p className="text-text-muted">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Our Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-primary to-secondary rounded-2xl shadow-xl p-8 md:p-12 mb-12 text-white"
        >
          <div className="flex items-start space-x-4">
            <div className="bg-white/20 p-3 rounded-lg">
              <UserGroupIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4">Our Story</h2>
              <p className="text-lg leading-relaxed mb-4 text-white/90">
                During my IIT JEE preparation journey, I stepped into a whole new era of digital learning. Online classes were packed with information, and it felt impossible to capture everything in my notes. I often spent hours trying to organize content, only to feel that I was falling behind. It was frustrating, exhausting, and overwhelming — yet I knew there had to be a better way.
              </p>
              <p className="text-lg leading-relaxed text-white/90">
                That’s when NotesCrafter was born. We created a platform that turns digital lessons and slides into clear, printable notes, saving precious time, reducing effort, and allowing students to focus on learning, understanding, and achieving their goals. Today, thousands of students trust NotesCrafter to transform the way they study, making exam preparation more efficient, productive, and even enjoyable.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-2xl p-8 md:p-12 mb-12"
        >
          <h2 className="text-3xl font-bold text-text-main mb-6 text-center">Powered by Modern Browser Technology</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-4">
              <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <CpuChipIcon className="w-6 h-6 text-white" />
              </div>
              <div className="font-semibold text-text-main">PDF-lib</div>
              <div className="text-sm text-text-muted">PDF Processing</div>
            </div>
            <div className="p-4">
              <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <GlobeAltIcon className="w-6 h-6 text-white" />
              </div>
              <div className="font-semibold text-text-main">PDF.js</div>
              <div className="text-sm text-text-muted">PDF Rendering</div>
            </div>
            <div className="p-4">
              <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <BoltIcon className="w-6 h-6 text-white" />
              </div>
              <div className="font-semibold text-text-main">WebAssembly</div>
              <div className="text-sm text-text-muted">Fast Computation</div>
            </div>
            <div className="p-4">
              <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-white" />
              </div>
              <div className="font-semibold text-text-main">Browser Security</div>
              <div className="text-sm text-text-muted">Local Processing</div>
            </div>
          </div>
        </motion.div>

        {/* Team & Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-2xl p-8 md:p-12 text-center"
        >
          <h2 className="text-3xl font-bold text-text-main mb-4">Join Our Journey</h2>
          <p className="text-lg text-text-muted mb-8 max-w-2xl mx-auto">
            We're constantly improving and adding new features based on user feedback. 
            Have a suggestion or want to learn more about NotesCrafter?
          </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page: 'contact-us' } }))}
              className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow"
            >
              Get in Touch
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUs;
